/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/shared/RecordTable.tsx
 * PURPOSE: CRM data table — lg+ horizontal scroll table; below md card list with expandable extra fields; sort, bulk, selection
 * DEPENDENCIES: react, @heroicons/react/24/outline, ../../../../layout/mobileShell, ./BulkActionBar
 * PUBLIC_API: RecordTable, RecordTableHandle, RecordTableColumn, RecordTableProps, RecordTableBulkProps
 * SIDE_EFFECTS: Invokes onRowClick / onSort from user gestures; loading state exposes polite status for screen readers
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 535/560
 * RECENT: 2026-05-11 Task 9 #90 — selection chrome gated by **`CRM_TABLE_CONFIG.BULK_ACTIONS_ENABLED`** (**`constants/crmTableConfig.ts`**)
 * RECENT_PRIOR: 2026-05-11 [2.2.943] — Numeric cell **`toLocaleString`** uses **`DEFAULT_CRM_LOCALE`** (**`en-GB`**) — root **`CHANGELOG` [2.2.943]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.890] Vitest **`bulk-clear-selection-btn`** — root **`CHANGELOG` [2.2.890]`**
 * RECENT_PRIOR: 2026-04-30 [2.2.752] Mobile card rows, lg+ table scroll — CHANGELOG [2.2.752]. [2.2.750] selectable + **`BulkActionBar`**
 */

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { KeyboardEvent, ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useMobileShell } from '../../../../layout/mobileShell';
import type { OpportunityStage } from '../../types/opportunity.types';
import { DEFAULT_CRM_LOCALE } from '../../utils/formatCurrency';
import { CRM_TABLE_CONFIG } from '../../constants/crmTableConfig';
import { BulkActionBar, type BulkActionBarVariant } from './BulkActionBar';

export type RecordTableHandle = {
  /** Restricts to row ids currently on this page; unknown ids are ignored. */
  setSelectedIds: (ids: string[]) => void;
};

export type RecordTableColumn = {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: Record<string, unknown>) => ReactNode;
  /** Prefer tabular figures for this column (also auto when cell value is a number). */
  numeric?: boolean;
};

export type RecordTableBulkProps = {
  variant: BulkActionBarVariant;
  onAssignOwner?: (ids: string[], ownerId: string) => void | Promise<void>;
  onAddTag?: (ids: string[], tag: string) => void | Promise<void>;
  onMoveStage?: (ids: string[], stage: OpportunityStage) => void | Promise<void>;
  onAdvanceStage?: (ids: string[]) => void | Promise<void>;
  onExport: (ids: string[]) => void;
  onDelete: (ids: string[]) => void | Promise<void>;
};

export type RecordTableProps = {
  /** Visually hidden table caption for assistive tech (e.g. drill-down title). */
  caption?: string;
  columns: RecordTableColumn[];
  data: Record<string, unknown>[];
  loading?: boolean;
  /** Required designed empty state (use `EmptyState` with a working `action.onClick`). */
  empty: ReactNode;
  /** Optional stable `data-testid` per body row (e.g. E2E for contact id). */
  rowTestId?: (row: Record<string, unknown>) => string | undefined;
  onRowClick?: (row: Record<string, unknown>) => void;
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  stickyHeader?: boolean;
  /** When true, first column is row selection checkboxes and optional bulk toolbar renders above the table. */
  selectable?: boolean;
  /** Row id field for selection keys (default `id`). */
  rowIdKey?: string;
  onSelectionChange?: (ids: string[]) => void;
  /** Required when `selectable` — wires bulk actions for the toolbar. */
  bulk?: RecordTableBulkProps;
};

function isNumericCell(column: RecordTableColumn, value: unknown): boolean {
  if (column.numeric) return true;
  return typeof value === 'number' && Number.isFinite(value);
}

function formatCellValue(value: unknown): ReactNode {
  if (value == null) return '—';
  if (typeof value === 'number') return value.toLocaleString(DEFAULT_CRM_LOCALE, { maximumFractionDigits: 0 });
  return String(value);
}

function rowStableId(row: Record<string, unknown>, idx: number, rowIdKey: string): string {
  const raw = row[rowIdKey] ?? row.id ?? row.key;
  if (raw != null && String(raw) !== '') return String(raw);
  return `__row_${idx}`;
}

function renderDataCell(column: RecordTableColumn, row: Record<string, unknown>): ReactNode {
  const raw = row[column.key];
  const numeric = isNumericCell(column, raw);
  const cell = column.render ? column.render(row) : formatCellValue(raw);
  return (
    <span className={['text-gray-800 dark:text-gray-200', numeric ? 'tabular-nums' : ''].filter(Boolean).join(' ')}>
      {cell}
    </span>
  );
}

export const RecordTable = forwardRef<RecordTableHandle, RecordTableProps>(function RecordTable(
  {
    caption,
    columns,
    data,
    loading,
    empty,
    rowTestId,
    onRowClick,
    sortKey,
    sortDir = 'asc',
    onSort,
    stickyHeader,
    selectable = false,
    rowIdKey = 'id',
    onSelectionChange,
    bulk,
  },
  ref
) {
  const { isMobileShell } = useMobileShell();
  /** Single shared gate — do not branch per hub page; flip in **`crmTableConfig`** when bulk ships. */
  const rowSelectionUiEnabled = Boolean(selectable && CRM_TABLE_CONFIG.BULK_ACTIONS_ENABLED);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const selectAllRef = useRef<HTMLInputElement>(null);

  const pageIds = useMemo(
    () => (!loading ? data : []).map((row, idx) => rowStableId(row, idx, rowIdKey)),
    [data, loading, rowIdKey]
  );

  useEffect(() => {
    const valid = new Set(pageIds);
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, [pageIds]);

  useEffect(() => {
    onSelectionChange?.(Array.from(selected));
  }, [selected, onSelectionChange]);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
  }, []);

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) => {
      if (pageIds.length === 0) return new Set();
      const allOn = pageIds.every((id) => prev.has(id));
      if (allOn) return new Set();
      return new Set(pageIds);
    });
  }, [pageIds]);

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el || !rowSelectionUiEnabled) return;
    const allOn = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    const some = pageIds.some((id) => selected.has(id));
    el.indeterminate = some && !allOn;
    el.checked = allOn;
  }, [pageIds, selected, rowSelectionUiEnabled]);

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  useImperativeHandle(
    ref,
    () => ({
      setSelectedIds: (ids: string[]) => {
        const valid = new Set(pageIds);
        setSelected(new Set(ids.filter((id) => valid.has(id))));
      },
    }),
    [pageIds]
  );

  const theadClass = stickyHeader
    ? 'sticky z-[1] border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/95'
    : 'border-b border-gray-200 dark:border-gray-700';

  const colSpan = columns.length + (rowSelectionUiEnabled ? 1 : 0);

  const toggleCardExpanded = useCallback((rowId: string) => {
    setExpandedCards((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  const rootScrollClass = isMobileShell ? 'overflow-visible' : 'lg:overflow-x-auto';

  return (
    <div
      data-testid="crm-record-table"
      className={`min-w-0 max-w-full rounded-xl border border-gray-200 dark:border-gray-700 ${rootScrollClass}`}
    >
      {rowSelectionUiEnabled && bulk ? (
        <BulkActionBar
          selectedIds={selectedIds}
          variant={bulk.variant}
          onClear={clearSelection}
          onAssignOwner={
            bulk.variant === 'contacts' && bulk.onAssignOwner
              ? (ownerId) => void bulk.onAssignOwner!(selectedIds, ownerId)
              : undefined
          }
          onAddTag={
            bulk.variant === 'contacts' && bulk.onAddTag ? (tag) => void bulk.onAddTag!(selectedIds, tag) : undefined
          }
          onMoveStage={
            bulk.variant === 'deals' && bulk.onMoveStage
              ? (stage) => void bulk.onMoveStage!(selectedIds, stage)
              : undefined
          }
          onAdvanceStage={
            bulk.variant === 'deals' && bulk.onAdvanceStage
              ? () => void bulk.onAdvanceStage!(selectedIds)
              : undefined
          }
          onExport={() => bulk.onExport(selectedIds)}
          onDelete={() => void bulk.onDelete(selectedIds)}
        />
      ) : null}
      {loading ? (
        <div role="status" className="sr-only" data-testid="crm-record-table-loading-status">
          Loading table contents
        </div>
      ) : null}

      {isMobileShell ? (
        <div data-testid="crm-record-table-cards">
          {loading ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {Array.from({ length: 4 }).map((_, sk) => (
                <div key={`msk-${sk}`} className="space-y-2 px-3 py-4" data-testid="crm-record-table-skeleton-row">
                  <div className="h-4 w-full max-w-[12rem] animate-pulse rounded bg-gray-200 dark:bg-gray-600" />
                  <div className="h-4 w-full max-w-[8rem] animate-pulse rounded bg-gray-200 dark:bg-gray-600" />
                </div>
              ))}
            </div>
          ) : null}
          {!loading && data.length === 0 ? (
            <div className="px-3 py-8 text-center text-gray-600 dark:text-gray-400">{empty}</div>
          ) : null}
          {!loading && data.length > 0 && rowSelectionUiEnabled ? (
            <div className="flex min-h-[44px] items-center gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
              <span className="sr-only">Select all on this page</span>
              <input
                ref={selectAllRef}
                type="checkbox"
                data-testid="crm-record-table-select-all"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:ring-offset-gray-900"
                aria-label="Select all rows on this page"
                onChange={toggleSelectAll}
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">Select all</span>
            </div>
          ) : null}
          {!loading &&
            data.map((row, idx) => {
              const rowId = rowStableId(row, idx, rowIdKey);
              const isSelected = selected.has(rowId);
              const interactive = Boolean(onRowClick);
              const tid = rowTestId?.(row);
              const expanded = Boolean(expandedCards[rowId]);
              const primaryCols = columns.slice(0, 3);
              const restCols = columns.slice(3);
              const cardClass = [
                'w-full border-b border-gray-200 px-3 py-4 text-left text-sm last:border-b-0 dark:border-gray-700',
                interactive ? 'cursor-pointer active:scale-[0.99]' : '',
                isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-white dark:bg-gray-800/40',
              ]
                .filter(Boolean)
                .join(' ');
              const onKeyDownCard = interactive
                ? (e: KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick?.(row);
                    }
                  }
                : undefined;
              return (
                <div
                  key={rowId}
                  data-testid={tid}
                  className={cardClass}
                  role={interactive ? 'button' : undefined}
                  tabIndex={interactive ? 0 : undefined}
                  aria-selected={rowSelectionUiEnabled ? isSelected : undefined}
                  onClick={() => interactive && onRowClick?.(row)}
                  onKeyDown={onKeyDownCard}
                >
                  <div className="flex gap-3">
                    {rowSelectionUiEnabled ? (
                      <div
                        className="flex shrink-0 items-start pt-0.5"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          data-testid={`crm-record-table-select-row-${rowId}`}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:ring-offset-gray-900"
                          checked={isSelected}
                          aria-label={`Select row ${rowId}`}
                          onChange={() => toggleRow(rowId)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    ) : null}
                    <div className="min-w-0 flex-1 space-y-2">
                      {primaryCols.map((col) => (
                        <div key={col.key}>
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {col.label}
                          </div>
                          <div className="mt-0.5 break-words">{renderDataCell(col, row)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {restCols.length > 0 ? (
                    <div className="mt-2">
                      <button
                        type="button"
                        data-testid={`crm-record-table-card-expand-${rowId}`}
                        className="inline-flex min-h-[44px] w-full items-center justify-between rounded-lg px-2 py-2 text-sm font-medium text-primary-700 transition-transform hover:bg-gray-50 active:scale-95 dark:text-primary-400 dark:hover:bg-gray-800"
                        aria-expanded={expanded}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCardExpanded(rowId);
                        }}
                      >
                        View all fields
                        <ChevronDownIcon
                          className={`h-5 w-5 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
                          aria-hidden
                        />
                      </button>
                      {expanded ? (
                        <div className="mt-2 space-y-2 border-t border-gray-100 pt-2 dark:border-gray-700">
                          {restCols.map((col) => (
                            <div key={col.key}>
                              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{col.label}</div>
                              <div className="mt-0.5">{renderDataCell(col, row)}</div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
        </div>
      ) : null}

      {!isMobileShell ? (
        <div className="lg:overflow-x-auto">
          <table
          className="min-w-full divide-y divide-gray-200 text-left text-sm dark:divide-gray-700"
          aria-busy={loading ? true : undefined}
        >
          {caption ? (
            <caption className="sr-only">{caption}</caption>
          ) : null}
          <thead
            className={theadClass}
            style={stickyHeader ? { top: 'var(--crm-record-table-sticky-top, 6.5rem)' } : undefined}
          >
            <tr>
              {rowSelectionUiEnabled ? (
                <th scope="col" className="w-4 px-1 py-2 align-middle">
                  <span className="sr-only">Select all on this page</span>
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    data-testid="crm-record-table-select-all"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:ring-offset-gray-900"
                    aria-label="Select all rows on this page"
                    onChange={toggleSelectAll}
                  />
                </th>
              ) : null}
              {columns.map((col) => {
                const active = sortKey === col.key;
                const sortable = Boolean(col.sortable && onSort);
                const content = (
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {active ? <span className="text-xs text-gray-500">{sortDir === 'asc' ? '↑' : '↓'}</span> : null}
                  </span>
                );
                const ariaSort = sortable
                  ? active
                    ? sortDir === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                  : undefined;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={ariaSort}
                    className="whitespace-nowrap px-3 py-2 font-semibold text-gray-900 dark:text-white"
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className="min-h-[44px] rounded px-1 text-left transition-transform hover:text-primary-600 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500 active:scale-95 dark:hover:text-primary-400"
                        aria-label={
                          active
                            ? `${col.label}, sorted ${sortDir === 'asc' ? 'ascending' : 'descending'}, press to reverse`
                            : `Sort by ${col.label}`
                        }
                        onClick={() => onSort?.(col.key)}
                      >
                        {content}
                      </button>
                    ) : (
                      content
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800/40">
            {loading
              ? Array.from({ length: 5 }, (_, sk) => (
                  <tr key={`sk-${sk}`} aria-hidden data-testid="crm-record-table-skeleton-row">
                    {rowSelectionUiEnabled ? (
                      <td className="w-4 px-1 py-2">
                        <div className="h-4 w-4 animate-pulse motion-reduce:animate-none rounded bg-gray-200 dark:bg-gray-600" />
                      </td>
                    ) : null}
                    {columns.map((col) => (
                      <td key={col.key} className="px-3 py-2">
                        <div className="h-4 w-full max-w-[8rem] animate-pulse motion-reduce:animate-none rounded bg-gray-200 dark:bg-gray-600" />
                      </td>
                    ))}
                  </tr>
                ))
              : null}
            {!loading && data.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-3 py-8 text-center text-gray-600 dark:text-gray-400">
                  {empty}
                </td>
              </tr>
            ) : null}
            {!loading &&
              data.map((row, idx) => {
                const rowId = rowStableId(row, idx, rowIdKey);
                const isSelected = selected.has(rowId);
                const interactive = Boolean(onRowClick);
                const rowClass = [
                  interactive
                    ? 'cursor-pointer transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:hover:bg-gray-800/80 dark:focus-visible:bg-gray-800/80'
                    : '',
                  isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const onKeyDownRow = interactive
                  ? (e: KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick?.(row);
                      }
                    }
                  : undefined;
                const rowProps = interactive
                  ? ({
                      tabIndex: 0 as const,
                      className: rowClass || undefined,
                      onClick: () => onRowClick?.(row),
                      onKeyDown: onKeyDownRow,
                    } as const)
                  : ({
                      className: rowClass || undefined,
                    } as const);
                const tid = rowTestId?.(row);
                return (
                  <tr key={rowId} {...rowProps} data-testid={tid} aria-selected={rowSelectionUiEnabled ? isSelected : undefined}>
                    {rowSelectionUiEnabled ? (
                      <td className="w-4 px-1 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          data-testid={`crm-record-table-select-row-${rowId}`}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:ring-offset-gray-900"
                          checked={isSelected}
                          aria-label={`Select row ${rowId}`}
                          onChange={() => toggleRow(rowId)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    ) : null}
                    {columns.map((col) => {
                      const raw = row[col.key];
                      const numeric = isNumericCell(col, raw);
                      const cell = col.render ? col.render(row) : formatCellValue(raw);
                      return (
                        <td
                          key={col.key}
                          className={[
                            'px-3 py-2 text-gray-800 dark:text-gray-200',
                            numeric ? 'tabular-nums' : '',
                          ].join(' ')}
                        >
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
});
